import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, UserPlus, X } from 'lucide-react';
import toast from 'react-hot-toast';
import apiService from '../../services/apiService';

function onlyDigits(value) {
  return String(value || '').replace(/\D/g, '');
}

function parseSearchDate(value) {
  const trimmed = String(value || '').trim();
  const iso = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) return trimmed;
  const br = trimmed.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (br) return `${br[3]}-${br[2]}-${br[1]}`;
  return '';
}

function toDateInputValue(value) {
  if (!value) return '';
  const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : '';
}

function formatBirthDateDisplay(value) {
  const iso = toDateInputValue(value);
  if (!iso) return '';
  const [year, month, day] = iso.split('-');
  return `${day}/${month}/${year}`;
}

function formatCpf(value) {
  return onlyDigits(value).slice(0, 11);
}

function normalizeSearchText(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function patientLabel(patient) {
  if (!patient) return '';
  const parts = [];
  if (patient.cpf) parts.push(`CPF ${formatCpf(patient.cpf)}`);
  const birth = formatBirthDateDisplay(patient.birth_date);
  if (birth) parts.push(birth);
  if (patient.mother_name) parts.push(`mãe: ${patient.mother_name}`);
  if (patient.source === 'esus') parts.push('e-SUS');
  if (patient.source === 'local' || patient.id) parts.push('cadastrado');
  return parts.join(' · ');
}

function candidateToResult(candidate) {
  const record = candidate.record || {};
  return {
    key: candidate.key,
    source: candidate.source,
    id: record.existing_id || record.id || null,
    ...record,
  };
}

function pickPatientPayload(data) {
  const allowed = [
    'cpf', 'name', 'cns', 'birth_date', 'gender', 'mother_name', 'city_id',
    'street_type', 'street', 'district', 'number', 'complement', 'reference_point',
    'zip_code', 'mobile_phone', 'whatsapp_phone', 'home_phone', 'contact_phone',
    'email', 'accessibility', 'active',
    'companion_name', 'companion_phone', 'companion_document', 'companion_cns',
    'companion_birth_date', 'companion_gender', 'companion_mother_name',
    'companion_city_id', 'companion_zip_code', 'companion_street',
  ];

  return allowed.reduce((acc, key) => {
    const value = data[key];
    if (value === undefined || value === null || value === '') return acc;
    acc[key] = value;
    return acc;
  }, { active: true });
}

export default function PatientLookupSelect({
  value,
  selectedPatient,
  onSelect,
  onClear,
  inputClassName,
  required = true,
}) {
  const [term, setTerm] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const [esusHit, setEsusHit] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [draft, setDraft] = useState({ cpf: '', name: '', birth_date: '' });
  const lastLookupCpfRef = useRef('');
  const requestIdRef = useRef(0);

  const selected = selectedPatient || (value ? { id: value, name: `Passageiro #${value}` } : null);

  useEffect(() => {
    if (!showCreate) return;
    setDraft((prev) => (
      prev.birth_date || !birthDate
        ? prev
        : { ...prev, birth_date: birthDate }
    ));
  }, [birthDate, showCreate]);

  useEffect(() => {
    const dateFromTerm = parseSearchDate(term);
    const query = dateFromTerm ? '' : term.trim();
    const effectiveBirthDate = birthDate || dateFromTerm;
    const canSearch = query.length >= 2 || Boolean(effectiveBirthDate);
    if (!canSearch) {
      setResults([]);
      setEsusHit(null);
      return undefined;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    const timer = setTimeout(async () => {
      try {
        setLoading(true);
        let nextResults = [];

        if (effectiveBirthDate) {
          const response = await apiService.lookupPatientsByBirthDate(effectiveBirthDate);
          if (requestId !== requestIdRef.current) return;
          nextResults = (response.data?.candidates || []).map(candidateToResult);
          if (query) {
            const normalized = normalizeSearchText(query);
            const digits = onlyDigits(query);
            nextResults = nextResults.filter((patient) => {
              const nameMatch = normalizeSearchText(patient.name).includes(normalized);
              const motherMatch = normalizeSearchText(patient.mother_name).includes(normalized);
              const cpfMatch = digits && formatCpf(patient.cpf).includes(digits);
              return nameMatch || motherMatch || cpfMatch;
            });
          }
        } else {
          const response = await apiService.listTransport('patients', {
            search: query,
            limit: 20,
            page: 1,
          });
          if (requestId !== requestIdRef.current) return;
          nextResults = (response.success ? (response.data?.results || []) : []).map((patient) => ({
            ...patient,
            key: `local-${patient.id}`,
            source: 'local',
          }));
        }

        setResults(nextResults);
      } catch (error) {
        if (requestId !== requestIdRef.current) return;
        console.error('Erro ao buscar passageiros:', error);
        setResults([]);
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    }, 350);

    return () => clearTimeout(timer);
  }, [term, birthDate]);

  useEffect(() => {
    const digits = onlyDigits(term);
    if (digits.length !== 11) {
      lastLookupCpfRef.current = '';
      setEsusHit(null);
      return undefined;
    }
    if (lastLookupCpfRef.current === digits) return undefined;

    let cancelled = false;
    lastLookupCpfRef.current = digits;

    (async () => {
      try {
        setLookingUp(true);
        const response = await apiService.lookupEsusCidadaoByCpf(digits, 'patient');
        if (cancelled || !response.success || !response.data) return;

        if (response.data.source === 'local' && response.data.existing_id) {
          setEsusHit(null);
          setResults((prev) => {
            if (prev.some((item) => Number(item.id) === Number(response.data.existing_id))) {
              return prev;
            }
            return [{
              id: response.data.existing_id,
              ...response.data,
            }, ...prev];
          });
          return;
        }

        setEsusHit(response.data);
        setDraft({
          cpf: formatCpf(response.data.cpf || digits),
          name: response.data.name || '',
          birth_date: toDateInputValue(response.data.birth_date) || birthDate,
        });
        setShowCreate(true);
      } catch (error) {
        if (!cancelled) {
          setEsusHit(null);
        }
      } finally {
        if (!cancelled) setLookingUp(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [term, birthDate]);

  const emptyMessage = useMemo(() => {
    if (loading || lookingUp) return 'Buscando...';
    if (term.trim().length < 2 && !birthDate) return 'Digite o nome, CPF ou a data de nascimento (dd/mm/aaaa).';
    return 'Nenhum cidadão encontrado no cadastro local ou no e-SUS.';
  }, [birthDate, loading, lookingUp, term]);

  const createPatient = async (data) => {
    const payload = pickPatientPayload({
      ...data,
      cpf: formatCpf(data.cpf),
      birth_date: toDateInputValue(data.birth_date) || null,
    });

    if (!payload.name) {
      toast.error('Informe o nome do passageiro');
      return null;
    }

    try {
      setCreating(true);
      const response = await apiService.createTransport('patients', payload);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Não foi possível cadastrar o passageiro');
      }
      toast.success('Passageiro cadastrado');
      onSelect(response.data);
      setShowCreate(false);
      setTerm('');
      setEsusHit(null);
      return response.data;
    } catch (error) {
      toast.error(error.message || 'Erro ao cadastrar passageiro');
      return null;
    } finally {
      setCreating(false);
    }
  };

  const handleDraftCpfChange = async (rawValue) => {
    const cpf = formatCpf(rawValue);
    setDraft((prev) => ({ ...prev, cpf }));

    if (cpf.length !== 11) return;

    try {
      setLookingUp(true);
      const response = await apiService.lookupEsusCidadaoByCpf(cpf, 'patient');
      if (!response.success || !response.data) return;

      if (response.data.source === 'local' && response.data.existing_id) {
        onSelect({
          id: response.data.existing_id,
          ...response.data,
        });
        toast.success('Passageiro encontrado no cadastro local');
        setShowCreate(false);
        return;
      }

      setEsusHit(response.data);
      setDraft({
        cpf,
        name: response.data.name || '',
        birth_date: toDateInputValue(response.data.birth_date) || birthDate,
      });
      toast.success('Dados encontrados no e-SUS');
    } catch (error) {
      toast.error(error.message || 'Usuário não cadastrado no e-SUS do município');
    } finally {
      setLookingUp(false);
    }
  };

  if (selected && Number(selected.id) === Number(value)) {
    return (
      <div>
        <div className="flex items-center justify-between gap-3 rounded-xl border border-border bg-slate-50 px-3 py-2.5">
          <div className="min-w-0">
            <div className="truncate text-sm font-medium text-text">{selected.name || `Passageiro #${selected.id}`}</div>
            <div className="truncate text-xs text-muted">{patientLabel(selected).replace(`${selected.name} · `, '')}</div>
          </div>
          <button
            type="button"
            onClick={() => {
              onClear?.();
              setTerm('');
              setBirthDate('');
              setResults([]);
              setEsusHit(null);
              setShowCreate(false);
            }}
            className="inline-flex items-center gap-1 rounded-lg border border-border bg-white px-2.5 py-1.5 text-xs font-medium text-muted hover:bg-slate-50 hover:text-text"
          >
            <X size={12} />
            Alterar
          </button>
        </div>
        {required && <input tabIndex={-1} value={value || ''} required onChange={() => {}} className="sr-only" />}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {required && <input tabIndex={-1} value={value || ''} required onChange={() => {}} className="sr-only" />}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[160px_minmax(0,1fr)]">
        <div>
          <label className="mb-1 block text-xs font-medium text-muted">Nascimento</label>
          <input
            type="date"
            value={birthDate}
            onChange={(event) => setBirthDate(event.target.value)}
            className={inputClassName}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-muted">Nome, CPF ou nascimento</label>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
            <input
              type="text"
              value={term}
              onChange={(event) => setTerm(event.target.value)}
              placeholder="Nome, CPF ou data (dd/mm/aaaa)"
              className={`${inputClassName} pl-9`}
            />
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-white">
        <ul className="max-h-52 overflow-y-auto py-1 text-sm">
          {results.map((patient) => (
            <li key={patient.key || patient.id}>
              <button
                type="button"
                disabled={creating}
                onClick={() => {
                  if (patient.id) {
                    onSelect(patient);
                    return;
                  }
                  createPatient(patient);
                }}
                className="w-full px-3 py-2 text-left hover:bg-primary/5 disabled:opacity-60"
              >
                <div className="font-medium text-text">
                  {patient.name}
                  {patient.source === 'esus' && !patient.id ? (
                    <span className="ml-2 text-xs font-medium text-primary">e-SUS · cadastrar</span>
                  ) : null}
                </div>
                <div className="text-xs text-muted">{patientLabel(patient) || 'Sem CPF/nascimento'}</div>
              </button>
            </li>
          ))}

          {esusHit && (
            <li className="border-t border-border">
              <button
                type="button"
                disabled={creating}
                onClick={() => createPatient(esusHit)}
                className="w-full px-3 py-2 text-left hover:bg-primary/5 disabled:opacity-60"
              >
                <div className="font-medium text-primary">Encontrado no e-SUS: {esusHit.name}</div>
                <div className="text-xs text-muted">
                  {formatBirthDateDisplay(esusHit.birth_date) || 'Sem nascimento'} · cadastrar e selecionar
                </div>
              </button>
            </li>
          )}

          {results.length === 0 && !esusHit && (
            <li className="px-3 py-2 text-muted">{emptyMessage}</li>
          )}
        </ul>
      </div>

      <button
        type="button"
        onClick={() => {
          setShowCreate((prev) => !prev);
          setDraft((prev) => ({
            cpf: formatCpf(term) || prev.cpf,
            name: onlyDigits(term).length === 11 ? prev.name : (term.trim() || prev.name),
            birth_date: prev.birth_date || birthDate,
          }));
        }}
        className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
      >
        <UserPlus size={14} />
        {showCreate ? 'Ocultar cadastro rápido' : 'Cadastrar novo passageiro'}
      </button>

      {showCreate && (
        <div className="grid grid-cols-1 gap-3 rounded-xl border border-dashed border-border bg-slate-50 p-3 sm:grid-cols-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-muted">CPF</label>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="off"
              maxLength={11}
              placeholder="11 dígitos"
              value={draft.cpf}
              disabled={lookingUp || creating}
              onChange={(event) => handleDraftCpfChange(event.target.value)}
              className={inputClassName}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted">Nome</label>
            <input
              type="text"
              value={draft.name}
              disabled={creating}
              onChange={(event) => setDraft((prev) => ({ ...prev, name: event.target.value }))}
              className={inputClassName}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted">Nascimento</label>
            <input
              type="date"
              value={draft.birth_date}
              disabled={creating}
              onChange={(event) => setDraft((prev) => ({ ...prev, birth_date: event.target.value }))}
              className={inputClassName}
            />
          </div>
          <div className="sm:col-span-3">
            <button
              type="button"
              disabled={creating || lookingUp}
              onClick={() => createPatient({ ...esusHit, ...draft })}
              className="rounded-xl bg-primary px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {creating ? 'Cadastrando...' : lookingUp ? 'Buscando...' : 'Cadastrar e selecionar'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
